import { AppSidebar } from "@/app/app-sidebar";
import { useAuth } from "@/auth";
import { Page } from "@/components/page";
import { PageCategory } from "@/components/page-header";
import { SidebarProvider } from "@/components/sidebar";
import { Toaster } from "@/components/toast";
import { useUnreadNotificationCount } from "@/http/api-hooks";
import { usePageTracking } from "@/lib/mixpanel/mixpanel-hooks";
import { useEffect } from "react";
import { Outlet, UIMatch, useMatches } from "react-router-dom";

function hasTitleHandle(m: UIMatch): m is UIMatch & { handle: { title: string } } {
  return (
    typeof m.handle === "object" &&
    m.handle !== null &&
    "title" in m.handle &&
    typeof (m.handle as { title?: unknown }).title === "string"
  );
}

export default function DashboardPage() {
  const auth = useAuth();
  const BASE_TITLE = "UA Dashboard";
  const matches = useMatches();
  const { unreadCount } = useUnreadNotificationCount();

  useEffect(() => {
    const matched = matches[matches.length - 1];
    const pageTitle = hasTitleHandle(matched) ? matched.handle.title : undefined;
    const baseTitle = pageTitle ? `${pageTitle} | ${BASE_TITLE}` : BASE_TITLE;
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  }, [matches, unreadCount]);

  usePageTracking({
    trackOnMount: true,
    trackOnLocationChange: true,
  });

  if (!auth.isAuthenticated) {
    return null;
  }

  const handle = matches[matches.length - 1].handle as {
    title: string;
    description: string;
    category: PageCategory;
    parent?: {
      title: string;
      path: string;
    };
    skipHeader?: boolean;
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <main className="flex-1 min-w-0 bg-background">
        <Page
          title={handle.title}
          description={handle.description}
          category={handle.category}
          parent={handle.parent}
          skipHeader={handle.skipHeader ?? false}
        >
          <Outlet />
          <Toaster position="bottom-right" />
        </Page>
      </main>
    </SidebarProvider>
  );
}
